import {Draggable} from '@hello-pangea/dnd';
import {ActionIcon, Box, Group, Menu, Stack, Text} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconDotsVertical,
  IconGripVertical,
  IconTrash,
} from '@tabler/icons-react';
import * as React from 'react';

export interface DraggableListItemContent {
  id: string;
  primary: string;
  primaryIcon?: React.ReactNode;
  secondary: string;
}

export interface DraggableListItemProps {
  content: DraggableListItemContent;
  index: number;
  selected: boolean;
  disabled?: boolean;
  onSelect: (itemId: string) => void;
  onDelete: (itemId: string) => void;
  onMoveUp?: (itemId: string) => void;
  onMoveDown?: (itemId: string) => void;
  isFirst?: boolean;
  isLast?: boolean;
}

const ICON_SIZE = 16;

export const DraggableListItem = React.memo<DraggableListItemProps>(
  ({
    content,
    index,
    selected,
    disabled,
    onSelect,
    onDelete,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
  }) => {
    const handleSelect = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(content.id);
      },
      [content.id, onSelect]
    );

    const handleDelete = React.useCallback(() => {
      onDelete(content.id);
    }, [content.id, onDelete]);

    const handleMoveUp = React.useCallback(() => {
      onMoveUp?.(content.id);
    }, [content.id, onMoveUp]);

    const handleMoveDown = React.useCallback(() => {
      onMoveDown?.(content.id);
    }, [content.id, onMoveDown]);

    const listItemContent = (
      <Box
        w="100%"
        px="xs"
        py={6}
        onClick={handleSelect}
        style={{
          cursor: 'pointer',
          background: selected
            ? 'var(--mantine-color-gray-1)'
            : disabled
            ? 'var(--mantine-color-gray-0)'
            : 'transparent',
          borderLeft: selected
            ? '3px solid var(--mantine-color-blue-5)'
            : '3px solid transparent',
        }}>
        <Group gap="xs" wrap="nowrap" align="center">
          <Box c={selected ? 'blue' : 'dimmed'} style={{flexShrink: 0}}>
            <IconGripVertical size={ICON_SIZE} />
          </Box>

          <Stack gap={0} style={{flex: 1, minWidth: 0}}>
            <Group gap={4} wrap="nowrap" align="center" style={{minWidth: 0}}>
              {content.primaryIcon}
              <Text
                size="sm"
                fw={400}
                c={disabled ? 'dimmed' : undefined}
                truncate="end"
                style={{flex: 1, minWidth: 0}}>
                {content.primary || '-'}
              </Text>
            </Group>
            <Text c="dimmed" size="xs" truncate="end">
              {content.secondary || '-'}
            </Text>
          </Stack>

          <Menu
            shadow="md"
            width={160}
            position="bottom-end"
            withinPortal>
            <Menu.Target>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                aria-label={`Actions for ${content.primary}`}
                style={{flexShrink: 0}}>
                <IconDotsVertical size={ICON_SIZE} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onMoveUp && (
                <Menu.Item
                  leftSection={<IconArrowUp size={ICON_SIZE} />}
                  onClick={handleMoveUp}
                  disabled={isFirst}>
                  Move up
                </Menu.Item>
              )}
              {onMoveDown && (
                <Menu.Item
                  leftSection={<IconArrowDown size={ICON_SIZE} />}
                  onClick={handleMoveDown}
                  disabled={isLast}>
                  Move down
                </Menu.Item>
              )}
              {(onMoveUp || onMoveDown) && <Menu.Divider />}
              <Menu.Item
                leftSection={<IconTrash size={ICON_SIZE} />}
                onClick={handleDelete}
                color="red">
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Box>
    );

    if (disabled) {
      return (
        <div
          role="option"
          aria-selected={selected}
          aria-label={content.primary}
          tabIndex={0}>
          {listItemContent}
        </div>
      );
    }

    return (
      <Draggable draggableId={content.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            role="option"
            aria-selected={selected}
            aria-label={content.primary}
            tabIndex={0}
            style={{
              ...provided.draggableProps.style,
              background: snapshot.isDragging
                ? 'var(--mantine-color-gray-2)'
                : 'transparent',
            }}>
            {listItemContent}
          </div>
        )}
      </Draggable>
    );
  }
);
